import { useDraggable } from '@dnd-kit/core';
import { useState } from 'react';
import { getLocalBlocksByCategory, getLocalCategories } from '@/lib/email-builder/local-block-definitions';
import { DocumentOutlinePanel } from './DocumentOutlinePanel';
import { MediaPanel } from './MediaPanel';
import { BlockIcon } from './BlockIcon';
import type { BlockDefinition, EmailBlockCategory } from '@/types/block-definitions';
import { SECTION_TEMPLATES, type SectionTemplate } from '@/lib/email-builder/section-templates';

/**
 * Individual draggable palette item component
 * Allows users to drag block types from the palette into the canvas
 */
interface DraggablePaletteItemProps {
  blockDefinition: BlockDefinition;
}

function DraggablePaletteItem({ blockDefinition }: DraggablePaletteItemProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${blockDefinition.blockType}`,
    data: {
      type: 'new-block',
      blockType: blockDefinition.blockType,
    },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`lw-block-palette-item cursor-move rounded border border-border bg-background px-1.5 py-1 hover:border-primary hover:bg-accent transition-colors ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
      title={blockDefinition.description}
    >
      <div className="flex items-center gap-1.5">
        <div className="h-5 w-5 flex items-center justify-center text-muted-foreground shrink-0">
          <BlockIcon iconName={blockDefinition.iconUrl || blockDefinition.blockType} className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-foreground leading-tight truncate">
            {blockDefinition.displayName}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Draggable section template item
 */
interface DraggableSectionTemplateProps {
  template: SectionTemplate;
}

function DraggableSectionTemplate({ template }: DraggableSectionTemplateProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${template.id}`,
    data: {
      type: 'new-block',
      blockType: template.id,
    },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`lw-block-palette-template-item cursor-move rounded border border-border bg-background px-1.5 py-1 hover:border-primary hover:bg-accent transition-colors ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
      title={template.description}
    >
      <div className="flex items-center gap-1.5">
        <div className="h-5 w-5 flex items-center justify-center text-muted-foreground shrink-0">
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a2 2 0 00-2 2v4m0 8v4a2 2 0 002 2h4m8 0h4a2 2 0 002-2v-4m0-8V6a2 2 0 00-2-2h-4" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-foreground leading-tight truncate">
            {template.displayName}
          </div>
        </div>
      </div>
    </div>
  );
}

export function BlockPalette() {
  const [activeTab, setActiveTab] = useState<'blocks' | 'outline' | 'media'>('blocks');

  // Get all categories from local definitions
  const categories = getLocalCategories();
  
  const [expandedCategory, setExpandedCategory] = useState<EmailBlockCategory | null>(
    categories[0] || null
  );

  const toggleCategory = (category: EmailBlockCategory) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  return (
    <div className="lw-block-palette flex h-full flex-col">
      {/* Tabs */}
      <div className="lw-block-palette-tabs flex border-b border-border">
        <button
          onClick={() => setActiveTab('blocks')}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'blocks'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Blocks
        </button>
        <button
          onClick={() => setActiveTab('outline')}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'outline'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Outline
        </button>
        <button
          onClick={() => setActiveTab('media')}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'media'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Media
        </button>
      </div>

      {/* Content */}
      <div className="lw-block-palette-content flex-1 overflow-y-auto p-4">
        {activeTab === 'blocks' ? (
          <div className="space-y-2">
            {categories.map((category) => {
              const blocksInCategory = getLocalBlocksByCategory(category);
              
              // For Layout category, we'll show section templates instead of section/column blocks
              const showSectionTemplates = category === 'Layout';
              
              // For Layout, filter out section/column from blocks list (use templates instead)
              const displayBlocks = showSectionTemplates 
                ? blocksInCategory.filter(block => block.blockType !== 'section' && block.blockType !== 'column')
                : blocksInCategory;
              
              // Skip empty categories
              if (displayBlocks.length === 0 && !showSectionTemplates) return null;

              const isExpanded = expandedCategory === category;

              return (
                <div key={category} className="lw-block-palette-category rounded-md border border-border bg-card">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="flex w-full items-center justify-between px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
                  >
                    <span>{category}</span>
                    <svg
                      className={`h-4 w-4 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-border p-1.5 space-y-0.5">
                      {/* Show section templates for Layout category */}
                      {showSectionTemplates && SECTION_TEMPLATES.map((template) => (
                        <DraggableSectionTemplate
                          key={template.id}
                          template={template}
                        />
                      ))}
                      
                      {/* Show regular filtered blocks */}
                      {displayBlocks.map((blockDef) => (
                        <DraggablePaletteItem
                          key={blockDef.blockType}
                          blockDefinition={blockDef}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : activeTab === 'outline' ? (
          // Document outline tab
          <DocumentOutlinePanel />
        ) : (
          // Media tab
          <MediaPanel />
        )}
      </div>
    </div>
  );
}
